import React from 'react';
import { styled } from '@mui/material/styles';
import { radius } from '../../theme/tokens.radius';
import { typography } from '../../theme';

const INLINE_FLEX = 'inline-flex';

export type UIKitChipSize = 'medium' | 'small';

export interface UIKitChipBaseProps {
  htmlId?: string;
  size?: UIKitChipSize;
  label: React.ReactNode;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'disabled',
})<{ size: UIKitChipSize; disabled?: boolean }>(({ theme, size, disabled }) => ({
  display: INLINE_FLEX,
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',

  // Figma: Static chip → border radius 8, border 1
  borderRadius: radius.s,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'transparent',

  textAlign: 'center',
  whiteSpace: 'nowrap',

  // typografy and fontweight
  ...theme.typography.body2,

  fontWeight: typography.body2.semibold.fontWeight,
  fontSize:
    size === 'small' ? typography.body3.semibold.fontSize : typography.body2.semibold.fontSize,

  // alturas y paddings desde Figma (state-layer)
  height: size === 'small' ? 24 : 32,
  padding: size === 'small' ? '4px 12px' : '6px 12px',

  background: 'transparent',
  outline: 'none',

  ...(disabled && { pointerEvents: 'none' }),
}));

const IconLeft = styled('span')({
  display: INLINE_FLEX,
  alignItems: 'center',
  marginRight: 4, // item spacing = 4
});

const IconRight = styled('span')({
  display: INLINE_FLEX,
  alignItems: 'center',
  marginLeft: 4,
});

const Label = styled('span')({
  display: INLINE_FLEX,
  alignItems: 'center',
});

const UIKitChipBase: React.FC<UIKitChipBaseProps> = ({
  htmlId,
  size = 'medium',
  label,
  iconLeft,
  iconRight,
  disabled = false,
  className,
  style,
}) => (
  <Root id={htmlId} size={size} disabled={disabled} className={className} style={style}>
    {iconLeft && <IconLeft>{iconLeft}</IconLeft>}
    <Label>{label}</Label>
    {iconRight && <IconRight>{iconRight}</IconRight>}
  </Root>
);

export default UIKitChipBase;
